import React, { useState, useCallback, useEffect, useMemo } from "react";
import { Button } from "../components/ui/button";
import { api, formatBRL } from "../lib/api";
import { DollarSign, FileCheck, Clock, AlertCircle, RefreshCw, FileDown, FileText } from "lucide-react";
import { toast } from "sonner";
import FileUpload from "../components/FileUpload";
import { exportCSV, exportPDF } from "../lib/export";
import DateRangeFilter from "../components/DateRangeFilter";

const PT_MONTHS = {
  janeiro: 0, fevereiro: 1, março: 2, abril: 3, maio: 4, junho: 5,
  julho: 6, agosto: 7, setembro: 8, outubro: 9, novembro: 10, dezembro: 11,
};

/** "Abril 2026" → Date no 1º dia do mês (UTC). null se não parsear. */
function monthLabelToDate(label) {
  if (!label) return null;
  const [m, y] = label.trim().toLowerCase().split(/\s+/);
  const month = PT_MONTHS[m];
  const year = Number(y);
  if (month === undefined || !year) return null;
  return new Date(Date.UTC(year, month, 1));
}

const MOCK_MONTHS = [
  { month: "Abril 2026", value: 18450.00, status: "parcial", nf_attached: false },
  { month: "Março 2026", value: 32100.50, status: "pago", nf_attached: true },
  { month: "Fevereiro 2026", value: 28750.00, status: "pago", nf_attached: true },
  { month: "Janeiro 2026", value: 21300.75, status: "pago", nf_attached: true },
];

const EXPORT_COLUMNS = [
  { key: "month", label: "Período" },
  { key: "value", label: "Valor", format: (v) => formatBRL(v) },
  { key: "status", label: "Status", format: (v) => (v === "pago" ? "Pago" : "Parcial") },
  { key: "nf_attached", label: "NF Anexada", format: (v) => (v ? "Sim" : "Não") },
];

export default function Commissions() {
  const [months, setMonths] = useState(MOCK_MONTHS);
  const [uploads, setUploads] = useState([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const filtered = useMemo(() => {
    if (!dateFrom && !dateTo) return months;
    const from = dateFrom ? new Date(`${dateFrom}T00:00:00Z`).getTime() : -Infinity;
    // "até" inclui o mês inteiro: empurra para o último dia
    let toMs = Infinity;
    if (dateTo) {
      const [y, m] = dateTo.split("-").map(Number);
      toMs = new Date(Date.UTC(y, m, 0, 23, 59, 59)).getTime(); // último dia do mês selecionado
    }
    return months.filter((row) => {
      const d = monthLabelToDate(row.month);
      if (!d) return true; // formatos desconhecidos passam
      const t = d.getTime();
      return t >= from && t <= toMs;
    });
  }, [months, dateFrom, dateTo]);

  const loadUploads = useCallback(async () => {
    try {
      const { data } = await api.get("/uploads?category=nf");
      setUploads(Array.isArray(data) ? data : []);
    } catch (e) {
      if (process.env.NODE_ENV === "development") console.warn("Failed to load uploads:", e);
    }
  }, []);

  useEffect(() => { loadUploads(); }, [loadUploads]);

  const handleUploadComplete = (monthIdx, uploadRecord) => {
    setMonths((prev) =>
      prev.map((m, i) => (i === monthIdx ? { ...m, nf_attached: true } : m))
    );
    loadUploads();
  };

  const currentMonth = months[0];

  return (
    <div className="space-y-6" data-testid="commissions-page">
      <div className="flex items-center justify-between">
        <div>
          <span className="zemo-overline">Financeiro</span>
          <h1 className="mt-1.5 text-3xl sm:text-[34px] font-semibold tracking-tight text-zinc-900">
            Comissionamento
          </h1>
          <p className="mt-1 text-sm text-zinc-500">
            Acompanhe suas comissões e anexe notas fiscais para liberação.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {uploads.length > 0 && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-xs text-emerald-700" data-testid="uploads-count-badge">
              <FileCheck className="h-3 w-3" />
              <span>{uploads.length} arquivo(s) enviado(s)</span>
            </div>
          )}
          <Button
            variant="outline"
            className="h-9"
            disabled={filtered.length === 0}
            onClick={() => exportCSV("comissoes-zemo", EXPORT_COLUMNS, filtered)}
            data-testid="comm-export-csv-btn"
          >
            <FileDown className="h-4 w-4 mr-1.5" /> CSV
          </Button>
          <Button
            variant="outline"
            className="h-9"
            disabled={filtered.length === 0}
            onClick={() => exportPDF("Relatório de Comissionamento", "comissoes-zemo", EXPORT_COLUMNS, filtered, { orientation: "portrait" })}
            data-testid="comm-export-pdf-btn"
          >
            <FileText className="h-4 w-4 mr-1.5" /> PDF
          </Button>
        </div>
      </div>

      {/* Date range filter (escopo do export) */}
      <div className="zemo-card p-3 flex items-center justify-between flex-wrap gap-2">
        <span className="text-[12px] text-zinc-500">Filtrar período exportado</span>
        <DateRangeFilter
          from={dateFrom}
          to={dateTo}
          onChange={({ from, to }) => {
            if (from !== undefined) setDateFrom(from);
            if (to !== undefined) setDateTo(to);
          }}
          testidPrefix="comm"
        />
      </div>

      {/* Current Month Highlight */}
      <div className="zemo-card p-6 border-l-4 border-l-[#0055FF]" data-testid="commission-current-month">
        <div className="flex items-start justify-between">
          <div>
            <span className="zemo-overline">Mês Atual — {currentMonth.month}</span>
            <div className="mt-2 text-3xl font-semibold tracking-tight font-mono-num text-zinc-900">
              {formatBRL(currentMonth.value)}
            </div>
            <div className="mt-1 flex items-center gap-2 text-[12px]">
              <Clock className="h-3.5 w-3.5 text-amber-500" />
              <span className="text-amber-700 font-semibold">Acumulado parcial</span>
              <span className="text-zinc-500">— valor pode aumentar até o fechamento</span>
            </div>
          </div>
          <div className="h-12 w-12 rounded-md bg-blue-50 border border-blue-200 flex items-center justify-center text-[#0055FF]">
            <DollarSign className="h-6 w-6" />
          </div>
        </div>
      </div>

      {/* Months Table */}
      <div className="zemo-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50/60 text-[11px] uppercase tracking-wider text-zinc-500 border-b border-[var(--zemo-border)]">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Período</th>
                <th className="text-right px-5 py-3 font-semibold">Valor</th>
                <th className="text-left px-5 py-3 font-semibold">Status</th>
                <th className="text-left px-5 py-3 font-semibold">Nota Fiscal</th>
                <th className="text-right px-5 py-3 font-semibold">Ação</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--zemo-border)]">
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-5 py-10 text-center text-sm text-zinc-500" data-testid="commission-empty">
                    Nenhum período no intervalo selecionado.
                  </td>
                </tr>
              )}
              {filtered.map((m) => {
                const idx = months.indexOf(m);
                return (
                <tr key={m.month} className="hover:bg-zinc-50/60" data-testid={`commission-row-${idx}`}>
                  <td className="px-5 py-4 font-semibold text-zinc-900">{m.month}</td>
                  <td className="px-5 py-4 text-right font-mono-num font-semibold text-zinc-900">
                    {formatBRL(m.value)}
                  </td>
                  <td className="px-5 py-4">
                    {m.status === "parcial" && (
                      <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200">
                        <Clock className="h-3 w-3" /> Parcial
                      </span>
                    )}
                    {m.status === "pago" && (
                      <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                        <FileCheck className="h-3 w-3" /> Pago
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-4">
                    {m.nf_attached ? (
                      <span className="text-[12px] text-emerald-700 font-semibold flex items-center gap-1">
                        <FileCheck className="h-3 w-3" /> Anexada
                      </span>
                    ) : (
                      <span className="text-[12px] text-zinc-400">Não anexada</span>
                    )}
                  </td>
                  <td className="px-5 py-4 text-right">
                    {!m.nf_attached && (
                      <FileUpload
                        category="nf"
                        onComplete={(record) => handleUploadComplete(idx, record)}
                      />
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent Uploads Section */}
      {uploads.length > 0 && (
        <div className="zemo-card overflow-hidden" data-testid="recent-uploads">
          <div className="px-5 py-3 border-b border-[var(--zemo-border)] flex items-center justify-between">
            <h2 className="text-sm font-semibold text-zinc-800">Uploads Recentes</h2>
            <Button variant="ghost" size="sm" onClick={loadUploads} data-testid="refresh-uploads-btn">
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>
          </div>
          <div className="divide-y divide-[var(--zemo-border)]">
            {uploads.slice(0, 5).map((u) => (
              <div key={u.id} className="px-5 py-3 flex items-center justify-between text-sm" data-testid={`upload-item-${u.id}`}>
                <div>
                  <span className="font-medium text-zinc-800">{u.original_filename}</span>
                  <span className="ml-2 text-xs text-zinc-400">{u.content_type}</span>
                </div>
                <ScanStatusBadge status={u.scan_status} />
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-md border border-blue-200 bg-blue-50 text-blue-900 text-[12px] p-3 flex items-start gap-2">
        <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
        <div>
          <strong>Processo:</strong> Após o fechamento do mês, anexe sua NF para liberar o pagamento.
          O backoffice validará o documento e efetuará a transferência em até 5 dias úteis.
          Arquivos aceitos: PDF, PNG, JPEG (máx 5MB). Todos os arquivos passam por escaneamento de segurança.
        </div>
      </div>
    </div>
  );
}

function ScanStatusBadge({ status }) {
  const config = {
    pending: { label: "Aguardando scan", className: "text-zinc-500 bg-zinc-50 border-zinc-200" },
    scanning: { label: "Escaneando", className: "text-amber-700 bg-amber-50 border-amber-200" },
    clean: { label: "Verificado", className: "text-emerald-700 bg-emerald-50 border-emerald-200" },
    infected: { label: "Rejeitado", className: "text-red-700 bg-red-50 border-red-200" },
    rejected: { label: "Inválido", className: "text-red-700 bg-red-50 border-red-200" },
  };
  const c = config[status] || config.pending;
  return (
    <span className={`inline-flex text-[11px] font-semibold px-2 py-0.5 rounded-full border ${c.className}`} data-testid="scan-status-badge">
      {c.label}
    </span>
  );
}
